import { useMemo } from "react";
import { useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { EntityLoadingState } from "../../components/ui/EntityPageChrome.jsx";
import { compactPrimaryButton } from "../../components/ui/buttonStyles.js";
import {
  PageErrorBanner,
  TypeCatalogCard,
  TypeCatalogSectionHeader,
  TypeCatalogToolbar,
} from "../../components/ui/TypeCatalogToolbar.jsx";
import { TypeCatalogEmptyState } from "../../components/emptyStates/TypeCatalogEmptyState.jsx";
import { Ico } from "../../components/icons/icons.jsx";
import { useAccountPermissions } from "../../logic/hooks/useAccountPermissions.js";
import { buildAccountDetailTokens } from "./accountDetailTheme.js";
import { AccountPermissionPagination } from "./components/AccountPermissionPagination.jsx";
import { AccountPermissionUserList } from "./components/AccountPermissionUserList.jsx";
import { AddAccountUserModal } from "./components/AddAccountUserModal.jsx";

export function AccountPermissionsTab({ account }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const vm = useAccountPermissions(account?.id);

  const filtered = vm.search.trim().length > 0;
  const countLabel = filtered ? `${vm.filteredCount} of ${vm.totalAll} users` : `${vm.totalAll} user${vm.totalAll === 1 ? "" : "s"}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <TypeCatalogSectionHeader
        title="Account users"
        subtitle={`Manage who can access ${account?.companyName ?? "this account"} and assign roles for each membership.`}
        action={
          <button type="button" onClick={() => vm.setAddOpen(true)} style={compactPrimaryButton(A)}>
            {Ico.plus}
            Add user
          </button>
        }
        c={c}
      />

      <PageErrorBanner message={vm.error} />

      <TypeCatalogCard ap={ap} dark={dark}>
        <TypeCatalogToolbar search={vm.search} onSearchChange={vm.setSearch} placeholder="Search by name or email…" countLabel={countLabel} c={c} />

        {vm.loading ? (
          <EntityLoadingState />
        ) : vm.totalAll === 0 ? (
          <TypeCatalogEmptyState c={c} A={A} icon={Ico.user} message="No users on this account" hint="Add team members to grant access and assign roles." onAction={() => vm.setAddOpen(true)} actionLabel="Add user" />
        ) : vm.filteredCount === 0 ? (
          <TypeCatalogEmptyState c={c} A={A} icon={Ico.user} message="No matching users" hint="Try a different search term." />
        ) : (
          <>
            <AccountPermissionUserList items={vm.items} ap={ap} saving={vm.saving} onChangeRole={vm.changeRole} />
            <AccountPermissionPagination
              page={vm.page}
              totalPages={vm.totalPages}
              pageSize={vm.pageSize}
              pageSizeOptions={vm.pageSizeOptions}
              rangeStart={vm.rangeStart}
              rangeEnd={vm.rangeEnd}
              total={vm.total}
              onPageChange={vm.setPage}
              onPageSizeChange={vm.setPageSize}
              c={c}
              A={A}
            />
          </>
        )}
      </TypeCatalogCard>

      <AddAccountUserModal open={vm.addOpen} onClose={() => vm.setAddOpen(false)} onSave={vm.addUser} saving={vm.saving} />
    </div>
  );
}
